import type { ChatMessage, ObjectUpdateItem } from '../types';

/** Match server-side mapper focus keys for profile skills */
export function buildProfileSkillFocusKey(profileId: string, skillName: string): string {
  const slug = skillName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `profile:${profileId}:skill:${slug || 'item'}`;
}

/** Normalize API/SSE fields into ObjectUpdateItem.action */
export function rawToObjectUpdateItem(raw: Record<string, unknown>): ObjectUpdateItem {
  const action =
    (raw.object_action as string) || (raw.action as string) || 'updated';
  return {
    object_type: (raw.object_type as string) || 'Object',
    object_id: (raw.object_id as string) || '',
    object_name: raw.object_name as string | undefined,
    action,
    source_tool: raw.source_tool as string | undefined,
    object_update_id: raw.object_update_id as string | undefined,
    description: raw.description as string | undefined,
    target_path: raw.target_path as string | undefined,
    focus_key: raw.focus_key as string | undefined,
    changed_fields: raw.changed_fields as string[] | undefined,
    display_label: raw.display_label as string | undefined,
  };
}

export function objectUpdateToItems(
  ou: ChatMessage['objectUpdate'] | undefined
): ObjectUpdateItem[] {
  if (!ou) return [];
  if (ou.items?.length) return ou.items;
  if (ou.object_type) {
    return [
      {
        object_type: ou.object_type,
        object_id: ou.object_id ?? '',
        object_name: ou.object_name,
        action: ou.action || 'updated',
        source_tool: ou.source_tool,
        object_update_id: ou.object_update_id,
        description: ou.description,
        target_path: ou.target_path,
        focus_key: ou.focus_key,
        changed_fields: ou.changed_fields,
        display_label: ou.display_label,
      },
    ];
  }
  return [];
}

export function getObjectUpdatePills(
  ou: ChatMessage['objectUpdate'] | undefined
): ObjectUpdateItem[] {
  return objectUpdateToItems(ou);
}

/** Single row from API → envelope with items for consistent UI */
export function normalizeObjectUpdateMessage(msg: ChatMessage): ChatMessage {
  if (msg.messageType !== 'object_update' || !msg.objectUpdate) return msg;
  const items = objectUpdateToItems(msg.objectUpdate);
  if (items.length === 0) return msg;
  return {
    ...msg,
    objectUpdate: { items },
  };
}

/** Merge consecutive persisted object_update rows that share run_id */
export function collapseObjectUpdateMessagesByRunId(
  messages: ChatMessage[]
): ChatMessage[] {
  const out: ChatMessage[] = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i];
    if (m.messageType !== 'object_update' || !m.runId) {
      out.push(m);
      i++;
      continue;
    }
    const runId = m.runId;
    const group: ChatMessage[] = [];
    while (
      i < messages.length &&
      messages[i].messageType === 'object_update' &&
      messages[i].runId === runId
    ) {
      group.push(messages[i]);
      i++;
    }
    if (group.length === 1) {
      out.push(normalizeObjectUpdateMessage(group[0]));
    } else {
      const items = group.flatMap(g => objectUpdateToItems(g.objectUpdate));
      const first = group[0];
      out.push({
        ...first,
        objectUpdate: { items },
      });
    }
  }
  return out;
}
